/**
 * The Great Tree — issue #140, and the proving slice for `docs/27-surface.md`.
 *
 * The surface is the one part of this world that is **authored rather than
 * generated**, and #140 says why: three rounds of #57 measured that layout
 * variety cannot be bought out of the generator without spending the movement
 * contract, and the other answer to the same complaint is to draw the frame by
 * hand. This file is the first landmark drawn.
 *
 * ==== WHAT SHAPE AUTHORED GEOMETRY TAKES HERE, AND WHY NOT THE OTHER TWO ====
 *
 * There were three shapes available and every one of them is already in the
 * tree:
 *
 * 1. **A `ChunkGenerator` decorator** — `withCrateSites` / `withDoorSites` /
 *    `withSupportSites` / `withRunDiffs`. A decorator sees a *finished* chunk:
 *    `mat`, `temp`, `flags`, `aux`. It cannot see `kind`, which is the array
 *    every structural pass in `worldgen.ts` reads and which `materialise`
 *    reduces into the block tags `annotate` hands the connectivity verifier. A
 *    tree stamped by a decorator would be a hundred thousand cells of solid
 *    wood that `Struct.EMPTY` tells the verifier is open sky. **Rejected: the
 *    decorator idiom is for things that decorate, and a landmark is terrain.**
 *
 * 2. **A total bypass** — `prologueGenerator()`, which fills GRANITE and
 *    replays a rect list, ignoring `(cx, cy)`'s usual meaning and the seed
 *    entirely. That is right for the prologue, which is a room with no world
 *    around it, and #118 made it skip `RunTerrain.bind` for exactly that
 *    reason. It is wrong here: the ground the tree stands on, the strata under
 *    it, the ore in those strata and the biome palette either side of it are
 *    all still the seed's. **Rejected: the surface is not a room.**
 *
 * 3. **A module that answers pure spatial queries, called from a generator
 *    pass** — `sanctum.ts`, `restNode.ts`, `setPiece.ts`, `supportSites.ts`.
 *    This is that, and it is the established idiom for authored geometry in
 *    this codebase. `worldgen.ts` calls `greatTreeKinds` inside its surface
 *    branch, before `materialise`, and `greatTreeDress` after it.
 *
 * ==== THE ONE ORACLE ====================================================
 *
 * `greatTreeAt(wx, wy)` is the whole shape. It returns a **material id**, or
 * `TREE_NONE` for "the tree is not here", and both generator passes read it:
 *
 *   - the **kind** pass turns the material into a `Kind` (`VOID` carves, a
 *     material with `M.oneWay` is a platform, everything else is solid), so
 *     the shape is visible to `materialise`'s block counters, to `annotate`,
 *     and therefore to the connectivity verifier;
 *   - the **dress** pass writes the material itself, after `materialise` has
 *     painted the chunk out of the biome palette.
 *
 * Two passes rather than one because `materialise` is "the one genuinely hot
 * loop in this file" and a landmark that exists at exactly one place in the
 * world has no business adding a branch to it. Splitting it costs a second
 * walk of the tree's bounding box in the two dozen chunks it touches and
 * nothing anywhere else.
 *
 * **One function rather than two is the testable part.** A shape declared once
 * and stamped twice cannot disagree with itself; kinds here and materials
 * there would be two transcriptions of one drawing, which is the defect
 * `tests/pixelTools.test.ts` §4 exists to catch elsewhere in this repo.
 *
 * ==== THE DETERMINISM BOUNDARY ==========================================
 *
 * **Nothing in this file reads the seed.** `RunTerrain`'s guard assumes a chunk
 * is a pure function of `(seed, cx, cy)` and a constant satisfies that
 * trivially — but the stronger property is the one #140's "mostly persistent
 * overworld" needs: *the Great Tree is the same tree in every world*, so a
 * returning player recognises it, and the surface can be given a persistence
 * model (`docs/27` §3) without the run seed having anything to say about it.
 * `tests/greatTree.test.ts` asserts both halves — identical tree cells across
 * eight seeds, and *non*-identical rock ten cells outside the trunk, so the
 * first assertion is capable of failing.
 *
 * The ground the tree stands on is made level by `WorldGen.surfaceHeight`'s
 * `TERRACE`, the same damping that already flattens the first screen around
 * spawn, which is why `GREAT_TREE.ground` can be a constant at all. That is the
 * boundary, stated once: **the authored frame pins the heightfield where a
 * landmark stands, and the seed owns every column outside it.**
 *
 * ==== THE SOFT GATE =====================================================
 *
 * The branch is the point of the whole slice. #16 removed key-and-lock from
 * this project ("the four abilities that are keys, not mechanics") and #132
 * rebuilt digging around routes for the same reason, so the tree does not have
 * a door. It has a **ceiling**.
 *
 * The climb is a stack of `SHELF_FUNGUS` rungs off the trunk's east face at
 * `RUNG_PITCH`, which is the descent shaft's landing idiom exactly: one-way, so
 * you rise through them and land on them, and `worldshape`'s standable rule
 * already knows a one-way row is not a ceiling. Then at `branchY` a bough two
 * hundred cells long comes out of the trunk, **solid**, and its underside is a
 * real ceiling over the last rung under it.
 *
 * The rule that decides whether a rung pair can be jumped is issue #105's, and
 * it is already written down in `tests/traversalAgent.ts`:
 *
 *     rise <= reach(room),   reach(room) = min(FREE_APEX 56, room - 2)
 *
 * Every rung below the bough clears it with room to spare. The step past the
 * bough does not, and **it fails on `room`, not on `rise`** — which is exactly
 * the difference between a gate and a wall. Stand under the bough, look up
 * through the daylight between its tip and the trunk, and the rungs carry on to
 * the crown. `tests/greatTree.test.ts` asserts the denial *and* carves the
 * bough back out of a generated world to prove the denial is the bough's doing
 * and not the ladder's spacing.
 *
 * ==== MATERIALS: THREE, AND THEY FOLLOW THE SHAPE =======================
 *
 * `docs/20` §3's Terraria finding is that an ore pass writes **zero `.active`**
 * — a vein inherits the silhouette of the rock it lands in — and `materialise`
 * already does that for veins. Authored landmarks are the same idea from the
 * other end: **one shape, one material, and the material is chosen before the
 * shape is drawn.** `biomes.ts` contains zero literal material references
 * because everything underground is sampled per cell from noise; this file
 * contains nothing *but* literal materials. The prologue does the same with
 * two and reads as a place.
 *
 *   - `WOOD` — the trunk's core, the boughs, the branch.
 *   - `PETRIFIED_WOOD` — the rind, `rind` cells thick, and the bough edges. An
 *     outline is what makes a silhouette read (#114's skin work), and the two
 *     sit 0x6b4a2a against 0x8a6b53: a value step, not a hue swap.
 *   - `SHELF_FUNGUS` — the rungs. Already the Rotwood Reach's one-way floor,
 *     and the surface wears band 0's palette; its own description is "bracket
 *     fungus in tiers off the wall". A ladder up a tree, made of the material
 *     that grows on trees, needs no explaining.
 *
 * `GLOWCAP` lights the hollow, on the 32-cell pitch `worldgen.ts` `glowVeins`
 * and `prologue.ts` both already use.
 */

import { M, VOID } from '../sim/materials';
import { MAT } from '../sim/materialData';

/** `greatTreeAt`'s "nothing here". Not a material id — `VOID` is 0 and carves. */
export const TREE_NONE = -1;

/**
 * The tree, as numbers. Sizes restated in `docs/20` §2's units in `docs/27` §5.
 *
 * In short: the trunk is 0.20 x 1.17 screens, the canopy 1.13 x 0.72, the whole
 * landmark 1.21 x 2.78 — against Terraria's Living Mahogany at 0.50 x 1.34 and
 * its Marble Cave at 0.65-1.23 x 0.58-0.85. Deliberately the largest single
 * object in the game, because there is exactly one of it in the world.
 */
export const GREAT_TREE = {
  /** Trunk centre. Far enough west that the grove is a walk and not a glance. */
  x: -900,
  /**
   * Ground line at the trunk. Equal to `SURFACE_Y` by construction —
   * `WorldGen.surfaceHeight` damps its relief to nothing across `TERRACE`, so
   * this is a fact about the world and not a hope about the noise.
   */
  ground: -40,
  /** Top of the trunk. 560 cells of climb above the ground line. */
  crownY: -600,
  /** Trunk half-width at the ground line, and at the crown. Linear taper. */
  hwBase: 64,
  hwTop: 30,
  /** Half-width and height of the root flare where the trunk meets the ground. */
  hwFlare: 100,
  flareH: 56,
  /** Thickness of the petrified rind on every wooden surface. */
  rind: 6,
  /** The hollow at the base: the room the pool sits in. */
  hollowHw: 44,
  hollowH: 42,
  /** The doorway, east face only, so the trunk still stands on its west flank. */
  mouthH: 28,
  /**
   * The soft gate: a solid bough off the east face, and the ceiling it makes.
   *
   * **Every one of these four numbers is load-bearing and the arithmetic is the
   * design.** `limbDepth` gives the bough a half-thickness of `branchTh / 2 + 2`
   * where it leaves the trunk, so its underside sits `branchY + 22` and its
   * upper surface `branchY - 22`. Against a body standing on `rungLowTop`:
   *
   *     room  = (branchY + 22) - (rungLowTop - 1) - 1 = 24
   *     reach = min(FREE_APEX 56, room - CEILING_COST 2) = 22
   *     rise  = rungLowTop - rungHigh = 53
   *
   * 53 > 22, so the fourteenth rung is denied — and 24 > 22, so the thirteenth
   * is still somewhere a body fits standing up, which is what makes it a place
   * you stop rather than a place you cannot be. Carve the bough away and `room`
   * becomes the open sky under the canopy, `reach` becomes `FREE_APEX`, and 53
   * is inside it by three cells. **That is the control**, and it is why the
   * bough is 20 thick rather than the 34 that would look better: at 34 the
   * fourteenth rung is out of reach whether the bough is there or not, and the
   * gate would be unfalsifiable.
   */
  branchY: -428,
  branchLen: 300,
  branchTh: 20,
  branchRise: -0.08,
  /** The climb. `SHELF_FUNGUS` rungs off the east face. */
  rungPitch: 25,
  rungLen: 34,
  rungTh: 4,
  /** Lowest rung, and the last one under the bough. */
  rungLow: -90,
  rungLowTop: -390,
  /**
   * How far above the bough's upper surface the ladder picks up again, and the
   * highest rung of all.
   *
   * `rungHigh` itself is **derived** (`firstRungOverBough`) rather than written
   * down, and that is the difference between a gate and a coincidence: with the
   * first rung over the bough pinned to a literal, thickening the bough moves
   * its underside without moving the rung, the control still passes, and the
   * test would go on being green for a bough that had stopped being falsifiable.
   * Derived, `branchTh` and the control are wired together and a mutation to
   * either is caught.
   */
  rungOverBough: 3,
  rungHighTop: -568,
  /** Canopy: boughs radiating from a point this far above the crown. */
  canopyDy: 30,
  /** The crown mass the boughs come out of. */
  crownRx: 82,
  crownRy: 52,
} as const;

/** The whole authored volume, as a clipping rect. Chunks outside it skip. */
export const GREAT_TREE_BOUNDS = {
  x0: GREAT_TREE.x - 290,
  x1: GREAT_TREE.x + 290,
  y0: -940,
  /**
   * One cell *above* the ground line, and this is not an off-by-one.
   *
   * Everything at `ground` and below belongs to `surfaceCarve` and to the pool
   * basin `pondDepth` cuts out of it. The first version of this bound was
   * `ground`, and the trunk's own root mass then wrote a solid row straight
   * across the top of its own basin: a sealed pool with a wooden lid on it,
   * which is the one thing `docs/27` §4.2 says must not be allowed to happen to
   * the pool. **A landmark stops at the ground line and the ground owns the
   * ground.**
   */
  y1: GREAT_TREE.ground - 1,
};

/**
 * The terrace: the interval of surface the landmark pins level.
 *
 * `WorldGen.surfaceHeight` reads this and damps its relief to zero inside it,
 * eased over `ease` either side, exactly as it already does within 150 cells of
 * spawn. Two things rest on it and both are load-bearing:
 *
 *  - **`GREAT_TREE.ground` is a constant**, so the tree is seed-invariant;
 *  - **the tree's foot is somewhere to stand and fight.** `worldshape`'s
 *    60-cell fighting floor is a bar the *underground* has to clear. A landmark
 *    the player walks nine hundred cells to reach and then cannot stand still
 *    in would be worse than the bands it is compensating for.
 */
export const TERRACE = { x0: -1010, x1: -790, ease: 140 };

/**
 * The pool, under the tree, inside the hollow — `docs/27` §4.2.
 *
 * It is the one element of #140's surface that **rewards rather than refuses**,
 * and it is the easiest thing in the design to lose: without it the opening
 * teaches "do not bother exploring" a run earlier than no pool at all would.
 * So it is stated here as a constant, `WorldGen` reads it instead of the three
 * module-private numbers it used to carry, and `tests/greatTree.test.ts` holds
 * its width, its depth, its material and the fact that you can get out again.
 *
 * Inside the hollow rather than out on the terrace for a mechanical reason:
 * a basin across the approach would be a body of water between the player and
 * the landmark, and "cross the water" is precisely the kind of thing that turns
 * into a `fluid-adapt` key. In the hollow it is a place you choose to go into.
 */
export const TREE_POOL = { cx: GREAT_TREE.x, rx: 40, depth: 32 };

/**
 * The hollow's reserved site, in world cells — `docs/27` §4.2.
 *
 * What stands on it is a scene-wiring decision and `src/core/game.ts` owns it.
 * What this file owes is a coordinate that is authored, reachable with no
 * ability, dry, and impossible for a generator pass to lose.
 */
export const TREE_HOLLOW_SITE = {
  x: GREAT_TREE.x + GREAT_TREE.hollowHw + 8,
  y: GREAT_TREE.ground - 1,
};

/** Trunk half-width at a world y, before the root flare. */
function trunkHalfWidth(wy: number): number {
  const t = (GREAT_TREE.ground - wy) / (GREAT_TREE.ground - GREAT_TREE.crownY);
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return GREAT_TREE.hwBase + (GREAT_TREE.hwTop - GREAT_TREE.hwBase) * c;
}

/**
 * The root flare added to the trunk near the ground.
 *
 * A squared falloff rather than a cone: a cone reads as a traffic bollard and
 * this has to read as something that grew. It is also what puts the doorway's
 * sill on level ground instead of on a slope.
 */
function flare(wy: number): number {
  const d = GREAT_TREE.ground - wy;
  if (d < 0 || d >= GREAT_TREE.flareH) return 0;
  const q = 1 - d / GREAT_TREE.flareH;
  return (GREAT_TREE.hwFlare - GREAT_TREE.hwBase) * q * q;
}

/**
 * The canopy, as boughs rather than as a filled ellipse.
 *
 * A solid ellipse would be a green cloud, and worse, it would be a lid:
 * `docs/27` §7 states the rule that a surface landmark may refuse the player
 * and may never refuse the sky. Seven tapering limbs give the same silhouette
 * with daylight between them, and they are the same primitive as the branch —
 * which is the point, because the branch has to read as *one of these* rather
 * than as a piece of level furniture bolted to a tree.
 *
 * A fixed table. No noise, no seed: see the determinism note in the header.
 */
const BOUGHS: readonly { dx: number; dy: number; len: number; th: number }[] = [
  { dx: -1.00, dy: -0.06, len: 200, th: 44 },
  { dx: -0.86, dy: -0.42, len: 190, th: 40 },
  { dx: -0.62, dy: -0.72, len: 165, th: 36 },
  { dx: -0.26, dy: -0.94, len: 150, th: 34 },
  { dx: 0.10, dy: -1.00, len: 145, th: 34 },
  { dx: 0.44, dy: -0.88, len: 158, th: 36 },
  { dx: 0.72, dy: -0.62, len: 178, th: 38 },
  { dx: 0.92, dy: -0.30, len: 196, th: 42 },
  { dx: 1.00, dy: 0.02, len: 205, th: 44 },
];

/**
 * The foliage, as clumps on the boughs.
 *
 * `SHELF_FUNGUS` and not a leaf material, because there is no leaf material and
 * adding one is `src/sim/materialData.ts`'s decision, not this file's. It is
 * also the better answer: bracket fungus in tiers is what actually grows on a
 * tree this old, it is already the Rotwood Reach's one-way floor so the surface
 * palette already contains it, and **one-way is the correct physics for a
 * canopy** — the crown is somewhere you can land and somewhere you rise
 * through, never a lid over the sky (`docs/27` §7).
 *
 * Derived from `BOUGHS` at module load rather than authored a second time: two
 * cell counts along each limb, so a bough that moves takes its leaves with it.
 */
const FOLIAGE: readonly { x: number; y: number; rx: number; ry: number }[] = (() => {
  const out: { x: number; y: number; rx: number; ry: number }[] = [];
  for (const b of BOUGHS) {
    const inv = 1 / Math.hypot(b.dx, b.dy);
    for (const [t, r] of [[0.62, 62], [0.99, 48]] as const) {
      out.push({
        x: Math.round(b.dx * inv * b.len * t),
        y: Math.round(b.dy * inv * b.len * t),
        rx: r,
        ry: Math.round(r * 0.72),
      });
    }
  }
  return out;
})();

/** Leaves on the gate bough, offset from `(GREAT_TREE.x, GREAT_TREE.branchY)`. */
const BRANCH_LEAVES: readonly { x: number; y: number; rx: number; ry: number }[] = [
  { x: 168, y: -13, rx: 56, ry: 40 },
  { x: 272, y: -21, rx: 42, ry: 31 },
];

/**
 * How far *inside* a tapering limb a point is, in cells; negative outside.
 *
 * The limb runs from `(ax, ay)` along `(dx, dy)` for `len`, half-thickness
 * `th / 2` at the root falling linearly to a 2-cell tip. One primitive for the
 * canopy and for the branch, so the gate is made of the same stuff as the
 * scenery.
 */
function limbDepth(
  px: number, py: number,
  ax: number, ay: number, dx: number, dy: number, len: number, th: number,
): number {
  const inv = 1 / Math.hypot(dx, dy);
  const ux = dx * inv;
  const uy = dy * inv;
  const rx = px - ax;
  const ry = py - ay;
  const t = rx * ux + ry * uy;
  if (t < 0 || t > len) return -1;
  const perp = Math.abs(rx * -uy + ry * ux);
  return (th * 0.5) * (1 - t / len) + 2 - perp;
}

/**
 * The upper surface of the gate bough where it leaves the trunk, and the first
 * rung above it.
 *
 * `limbDepth` gives the bough a half-thickness of `branchTh / 2 + 2` at its
 * root, so the bough occupies `branchY ± that` and the ladder resumes
 * `rungOverBough` cells clear of it. Derived, not authored — see the note on
 * `rungOverBough`.
 */
export function boughTop(): number {
  return GREAT_TREE.branchY - Math.round(GREAT_TREE.branchTh / 2 + 2);
}

export function firstRungOverBough(): number {
  return boughTop() - GREAT_TREE.rungOverBough;
}

/** Every rung's top row, lowest (largest y) first. */
export function rungRows(): number[] {
  const T = GREAT_TREE;
  const out: number[] = [];
  for (let y = T.rungLow; y >= T.rungLowTop; y -= T.rungPitch) out.push(y);
  for (let y = firstRungOverBough(); y >= T.rungHighTop; y -= T.rungPitch) out.push(y);
  return out;
}

/**
 * The two rungs the gate is between: the last one you can reach and the first
 * one you cannot. Exported so the test names the same pair the design does.
 */
export function gateRungs(): { below: number; above: number } {
  return { below: GREAT_TREE.rungLowTop, above: firstRungOverBough() };
}

/**
 * What the Great Tree puts at a world cell.
 *
 * A material id, `VOID` where it carves, or `TREE_NONE` where it is absent.
 * This is the whole shape; both generator passes read it and nothing else.
 *
 * Order matters and is a build order, the way `prologue.ts`'s rect list is:
 * the hollow and the doorway are tested first so nothing below them can fill
 * the one room the player is meant to get into.
 */
export function greatTreeAt(wx: number, wy: number): number {
  const T = GREAT_TREE;
  if (wx < GREAT_TREE_BOUNDS.x0 || wx > GREAT_TREE_BOUNDS.x1) return TREE_NONE;
  if (wy < GREAT_TREE_BOUNDS.y0 || wy > GREAT_TREE_BOUNDS.y1) return TREE_NONE;

  const rx = wx - T.x;
  const ax = rx < 0 ? -rx : rx;

  // ---- 1. the hollow, its doorway, and the light inside it.
  if (wy > T.ground - T.hollowH && wy <= T.ground - 1 && ax <= T.hollowHw) return VOID;
  if (wy > T.ground - T.mouthH && wy <= T.ground - 1 && rx > 0 && rx <= T.hwFlare) return VOID;
  if (wy === T.ground - T.hollowH && ax <= T.hollowHw && ((rx + 96) % 32) < 10) {
    return MAT.GLOWCAP;
  }

  // ---- 2. the rungs. One-way, off the east face, at `rungPitch`.
  if (wy <= T.rungLow && wy >= T.rungHighTop) {
    for (const ry of rungRows()) {
      if (wy > ry || wy <= ry - T.rungTh) continue;
      const hw = trunkHalfWidth(wy);
      if (rx >= hw - 2 && rx <= hw + T.rungLen) return MAT.SHELF_FUNGUS;
    }
  }

  // ---- 3. the branch. Solid, and the only ceiling on the climb.
  {
    const d = limbDepth(wx, wy, T.x, T.branchY, 1, T.branchRise, T.branchLen, T.branchTh);
    if (d >= 0) return d < T.rind ? MAT.PETRIFIED_WOOD : MAT.WOOD;
  }
  // ...and its leaves, which are one-way and therefore not part of the gate.
  // They are what makes the bough read as huge without making it *deny* more:
  // `headroom` and `worldshape`'s standable rule both ignore a one-way cell as
  // ceiling, so a clump can be any size and the arithmetic above is untouched.
  // Every clump sits well east of the rungs, so there is nothing under one to
  // jump from — see `docs/27` §6.3.
  for (const f of BRANCH_LEAVES) {
    const dx = (wx - T.x - f.x) / f.rx;
    const dy = (wy - T.branchY - f.y) / f.ry;
    if (dx * dx + dy * dy <= 1) return MAT.SHELF_FUNGUS;
  }

  // ---- 4. the trunk, with its rind and its root flare.
  if (wy <= T.ground && wy >= T.crownY) {
    const hw = trunkHalfWidth(wy) + flare(wy);
    if (ax <= hw) return ax > hw - T.rind ? MAT.PETRIFIED_WOOD : MAT.WOOD;
  }

  // ---- 5. the canopy: the crown mass, then the boughs, then the leaves.
  //         Wood before fungus, so a bough reads through the clump it carries
  //         rather than being swallowed by it.
  const cy = T.crownY - T.canopyDy;
  {
    const dx = (wx - T.x) / T.crownRx;
    const dy = (wy - cy) / T.crownRy;
    const q = dx * dx + dy * dy;
    if (q <= 1) return q > 0.74 ? MAT.PETRIFIED_WOOD : MAT.WOOD;
  }
  for (const b of BOUGHS) {
    const d = limbDepth(wx, wy, T.x, cy, b.dx, b.dy, b.len, b.th);
    if (d >= 0) return d < T.rind ? MAT.PETRIFIED_WOOD : MAT.WOOD;
  }
  for (const f of FOLIAGE) {
    const dx = (wx - T.x - f.x) / f.rx;
    const dy = (wy - cy - f.y) / f.ry;
    if (dx * dx + dy * dy <= 1) return MAT.SHELF_FUNGUS;
  }

  return TREE_NONE;
}

/** Does this chunk's world rect meet the tree at all? The chunk-level early out. */
export function chunkTouchesGreatTree(x0: number, y0: number, x1: number, y1: number): boolean {
  return !(x1 < GREAT_TREE_BOUNDS.x0 || x0 > GREAT_TREE_BOUNDS.x1
    || y1 < GREAT_TREE_BOUNDS.y0 || y0 > GREAT_TREE_BOUNDS.y1);
}

/**
 * The `Kind` a tree material resolves to.
 *
 * The join between the two generator passes, exported so a test can assert the
 * join rather than re-derive it. `Kind` itself is `worldgen.ts`-private, so the
 * three codes are handed in — which also means this function cannot silently
 * disagree with the enum.
 */
export function treeKindOf(mat: number, kOpen: number, kPlatform: number, kSolid: number): number {
  if (mat === VOID) return kOpen;
  return M.oneWay[mat] !== 0 ? kPlatform : kSolid;
}
